import React, { useRef, useState } from 'react';
import type { ApprovalItem } from '../client';
import AsyncButton from '../components/AsyncButton';
import { ApprovalCardHeader } from './ApprovalCardHeader';
import './approvals.css';
import {
  EGRESS_RULE_CATALOG,
  EgressProtocol,
  formatPortBounds,
} from '../workspaces/egressRuleCatalog';

/** Friendly labels for well-known ports — display only, mirrors the backend's
 *  PORT_PROTOCOL_LABELS; enforcement never depends on this. */
const PORT_PROTOCOL_LABELS: Record<number, string> = {
  22: 'SSH',
  80: 'HTTP',
  443: 'HTTPS',
  3306: 'MySQL',
  5432: 'PostgreSQL',
  6379: 'Redis',
  27017: 'MongoDB',
};

/**
 * Approval card for the `network_egress` type: an agent requesting access to
 * a domain not already on the team's approved list. Surfaces the requested
 * domain (and port, if the request is port-specific) prominently alongside
 * the agent's stated reason.
 */
export const NetworkEgressApprovalCard: React.FC<{
  approval: ApprovalItem;
  deciding?: boolean;
  onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    inputs?: Record<string, unknown>,
  ) => Promise<void>;
}> = ({ approval, deciding, onDecide }) => {
  const [note, setNote] = useState('');
  const inFlight = useRef(false);
  const d = approval.detail || {};

  const domain = typeof d.domain === 'string' ? d.domain : null;
  const protocol =
    d.protocol === 'tcp' || d.protocol === 'udp' || d.protocol === 'icmp'
      ? (d.protocol as EgressProtocol)
      : null;
  const ruleType = typeof d.ruleType === 'string' ? d.ruleType : undefined;
  const portStart = typeof d.portStart === 'number' ? d.portStart : null;
  const portEnd = typeof d.portEnd === 'number' ? d.portEnd : null;
  const canonicalRule = protocol
    ? `${
        EGRESS_RULE_CATALOG.find((entry) => entry.key === ruleType)?.label ??
        protocol.toUpperCase()
      } · ${formatPortBounds(portStart, portEnd, ruleType)}`
    : null;
  const port =
    typeof d.port === 'number'
      ? d.port
      : typeof d.port === 'string' && d.port.trim() !== ''
        ? Number(d.port)
        : null;
  const portSuffix =
    port != null && Number.isFinite(port)
      ? PORT_PROTOCOL_LABELS[port]
        ? `:${port} (${PORT_PROTOCOL_LABELS[port]})`
        : `:${port}`
      : '';
  const status = typeof d.status === 'string' ? d.status : approval.status;
  const pending = approval.status === 'pending' && status === 'pending';

  const [pendingDecision, setPendingDecision] = useState<
    'approved' | 'rejected' | null
  >(null);

  const decide = async (decision: 'approved' | 'rejected') => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPendingDecision(decision);
    try {
      await onDecide(decision, note.trim());
      setNote('');
    } finally {
      inFlight.current = false;
      setPendingDecision(null);
    }
  };

  return (
    <article
      className="approval-card approval-card-chat"
      data-testid={`approval-${approval.id}`}>
      <ApprovalCardHeader
        teamId={approval.teamId}
        agent={approval.requestingAgent}
        requestingUser={approval.requestingUser}
        requestingTrigger={approval.requestingTrigger}
        origin={approval.origin}
        createdAt={approval.createdAt}
        title={approval.title}
      />

      {canonicalRule && (
        <p className="descriptionText" style={{ marginTop: 6 }}>
          Requested network access:{' '}
          <code data-testid="network-egress-rule">
            {domain ? `${domain} · ` : ''}
            {canonicalRule}
          </code>
        </p>
      )}
      {!canonicalRule && domain && (
        <p className="descriptionText" style={{ marginTop: 6 }}>
          Requested domain:{' '}
          <code data-testid="network-egress-domain">
            {domain}
            {portSuffix}
          </code>
        </p>
      )}
      {!canonicalRule && !domain && port != null && Number.isFinite(port) && (
        <p className="descriptionText" style={{ marginTop: 6 }}>
          Requested port:{' '}
          <code data-testid="network-egress-port">
            port {port}
            {PORT_PROTOCOL_LABELS[port]
              ? ` (${PORT_PROTOCOL_LABELS[port]})`
              : ''}
          </code>
        </p>
      )}

      <div className="approval-reason">
        <span>The agent's stated reason</span>
        <p>{approval.reason}</p>
      </div>

      {pending ? (
        <>
          <label className="approval-note">
            <span>
              Note to agent <small>(optional)</small>
            </span>
            <textarea
              aria-label="Note to agent"
              maxLength={4000}
              disabled={deciding}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add context, constraints, or next steps…"
            />
          </label>
          <div className="approval-actions">
            <AsyncButton
              type="button"
              className="approval-reject"
              disabled={deciding}
              loading={pendingDecision === 'rejected'}
              loadingText="Rejecting…"
              onClick={() => decide('rejected')}>
              Reject
            </AsyncButton>
            <AsyncButton
              type="button"
              className="approval-approve"
              disabled={deciding}
              loading={!!deciding || pendingDecision === 'approved'}
              loadingText="Saving…"
              onClick={() => decide('approved')}>
              Approve
            </AsyncButton>
          </div>
        </>
      ) : (
        <div
          className={`approval-invite-status approval-invite-status-${approval.status}`}
          role="status">
          {approval.status === 'approved'
            ? 'Network access approved.'
            : approval.status === 'rejected'
              ? `Rejected${approval.decisionNote ? ` — ${approval.decisionNote}` : '.'}`
              : `This request is ${approval.status}.`}
        </div>
      )}
    </article>
  );
};

export default NetworkEgressApprovalCard;
